import { add } from 'date-fns'
import {
  useLembreteProfissionalByInputQuery,
  useMarcarVisualizadoLembreteProfissionalMutation,
  useReagendarLembreteProfissionalMutation,
} from 'graphql/hooks.generated'
import { LembreteProfissionalIdentificadorEnum } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { useEffect, useMemo, useRef, useState } from 'react'
import { parseDateToTimestamp } from 'util/date/formatDate'

const DEFAULT_DIAS_VALIDADE = 30

export interface UseLembreteProfissionalComValidadeProps {
  usuarioId?: string
  identificador: LembreteProfissionalIdentificadorEnum
  /**
   * Define a validade em dias para o lembrete expirar.
   */
  diasValidade: number
}

export function useLembreteProfissionalComValidade({
  usuarioId,
  identificador,
  diasValidade,
}: UseLembreteProfissionalComValidadeProps) {
  const { getServerTimeNow } = useServerTime()
  const [reagendarMutation] = useReagendarLembreteProfissionalMutation()
  const [marcarVisualizadoMutation] = useMarcarVisualizadoLembreteProfissionalMutation()

  const [dataLimiteExibicao, setDataLimiteExibicao] = useState<number | null>(null)
  const diasDeValidadeNormalizado = normalizarDiasDeValidade(diasValidade)

  const lembreteJaReagendadoIdRef = useRef<string | null>(null)
  const marcadoExpiradoIdRef = useRef<string | null>(null)

  const input = useMemo(
    () => ({
      usuarioId,
      identificador,
    }),
    [usuarioId, identificador]
  )

  const { data, loading } = useLembreteProfissionalByInputQuery({
    variables: { input },
    skip: !usuarioId,
    fetchPolicy: 'network-only',
  })

  const lembrete = data?.lembreteProfissional
  const lembreteId = lembrete?.id
  const isVisualizado = !!lembrete?.visualizado
  const dataExibirNovamentePersistida = parseDateToTimestamp(lembrete?.dataExibirNovamente)

  useEffect(() => {
    setDataLimiteExibicao(dataExibirNovamentePersistida)
  }, [dataExibirNovamentePersistida])

  useEffect(() => {
    const jaTemDataLimiteDefinida = dataExibirNovamentePersistida !== null || !!dataLimiteExibicao
    const jaFoiReagendado = lembreteJaReagendadoIdRef.current === lembreteId
    const deveIgnorarReagendamento = !lembreteId || isVisualizado || jaTemDataLimiteDefinida || jaFoiReagendado

    if (deveIgnorarReagendamento) {
      return
    }

    const novaDataLimite = add(getServerTimeNow(), { days: diasDeValidadeNormalizado }).getTime()

    setDataLimiteExibicao(novaDataLimite)
    lembreteJaReagendadoIdRef.current = lembreteId

    void reagendarMutation({
      variables: {
        input: { id: lembreteId, dataExibirNovamente: novaDataLimite },
      },
    }).catch(() => {
      lembreteJaReagendadoIdRef.current = null
      setDataLimiteExibicao(null)
    })
  }, [
    dataExibirNovamentePersistida,
    dataLimiteExibicao,
    getServerTimeNow,
    isVisualizado,
    lembreteId,
    reagendarMutation,
    diasDeValidadeNormalizado,
  ])

  const isExpirado = useMemo(() => {
    if (!dataLimiteExibicao || isVisualizado) {
      return false
    }

    return dataLimiteExibicao <= getServerTimeNow().getTime()
  }, [dataLimiteExibicao, getServerTimeNow, isVisualizado])

  useEffect(() => {
    if (marcadoExpiradoIdRef.current === lembreteId) return

    if (!lembreteId || isVisualizado || !isExpirado) return

    marcadoExpiradoIdRef.current = lembreteId
    void marcarVisualizadoMutation({
      variables: { id: lembreteId },
    }).catch(() => {
      marcadoExpiradoIdRef.current = null
    })
  }, [isExpirado, isVisualizado, lembreteId, marcarVisualizadoMutation])

  const isLembreteValido = !!lembreteId && !isVisualizado
  const isLembreteDentroDeValidade = !dataLimiteExibicao || !isExpirado
  const isAtivo = !loading && isLembreteValido && isLembreteDentroDeValidade

  return {
    isAtivo,
    loading,
    lembrete,
  }
}

function normalizarDiasDeValidade(diasValidade: number): number {
  if (!Number.isFinite(diasValidade) || diasValidade <= 0) {
    return DEFAULT_DIAS_VALIDADE
  }

  return diasValidade
}
